import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Sparkles, Award, Clock } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { SignUpForm } from '../components/auth/SignUpForm';
import { useJWTAuth } from '../contexts/useJWTAuth';
import { analytics } from '../services/analytics/posthog';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useJWTAuth();

  useEffect(() => {
    analytics.pageViewed('signup');
  }, []);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleSuccess = () => {
    navigate('/dashboard', { state: { isFirstVisit: true } });
  };

  const handleSwitchToSignIn = () => {
    navigate('/signin');
  };

  // Don't render if user is already authenticated (while redirect happens)
  if (!isLoading && isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar with opposite auth link */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-1 px-6 lg:px-8 py-12 pt-24">
        <div className="max-w-7xl mx-auto">
          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Marketing Content */}
            <div className="hidden lg:block space-y-8">
              <div className="space-y-4">
                <h1
                  className="text-4xl font-bold leading-tight"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Start improving your code today
                </h1>
                <p
                  className="text-lg leading-relaxed"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Join thousands of developers using AI-powered code analysis to ship better
                  software, faster.
                </p>
              </div>

              {/* Feature Highlights */}
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--color-accent-teal)' }}
                  >
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      Quick Setup
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      Get started in under 5 minutes with easy onboarding
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--color-accent-blue)' }}
                  >
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      Smart Recommendations
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      AI reviews your code and suggests improvements
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--color-accent-green)' }}
                  >
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      Completely Safe
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      Your code stays untouched, we analyze a safe copy
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--color-accent-purple, #8b5cf6)' }}
                  >
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      Free to Start
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      No credit card required, upgrade anytime
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Auth Card */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <div
                className="bg-white dark:bg-gray-800 rounded-xl p-8"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-default)',
                }}
              >
                {/* Header */}
                <div className="text-center mb-8">
                  <h2
                    className="text-2xl font-semibold mb-2"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    Create your account
                  </h2>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    Get started with Autar in minutes
                  </p>
                </div>

                {/* Sign Up Form */}
                <SignUpForm onSuccess={handleSuccess} onSwitchToSignIn={handleSwitchToSignIn} />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SignUpPage;
